/**
 * Iter60.p55b — Incidents & Emergencies KPI tile.
 *
 * Renders on:
 *   * HR Home / Admin Home — full-width, all-modules aggregate + a
 *     per-module breakdown row underneath the top counters.
 *   * Module Dashboard — module-scoped (the tile call passes
 *     `moduleCode`, the API scopes the aggregate to that module).
 *
 * Visual:
 *   * Primary metric = `total_open` (large navy number).
 *   * Sub-metric = `N red flags this week` in crimson.
 *   * Footer = "N days without a red flag" — subtle gray by default,
 *     green ≥ 30d, gold ≥ 90d, celebratory brand color ≥ 180d.
 *   * When `total_open === 0`: friendly "All clear ✓" microcopy.
 *
 * Click:
 *   * HR / Admin (no `moduleCode` prop) → global `/incidents?status=open`.
 *   * Manager / scoped variant (with `moduleCode`) →
 *     `/m/{code}/incidents?status=open`.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

function DaysWithoutFooter({ days }) {
    // 999 sentinel = never had a red flag. Show as "All time clean".
    if (days === undefined || days === null) return null;
    const v = Number(days);
    let color = "var(--asl-text-3)";
    let icon = null;
    let label = `${v} day${v === 1 ? "" : "s"} without a red flag`;
    if (v >= 999) {
        color = "#2e804f";
        label = "No red flag on record — all time clean";
        icon = <ShieldCheck className="h-3 w-3" />;
    } else if (v >= 180) {
        color = "var(--asl-crimson)";
        icon = <ShieldCheck className="h-3 w-3" />;
        label = `${v} days without a red flag · milestone 180+`;
    } else if (v >= 90) {
        color = "#c99700";
        icon = <ShieldCheck className="h-3 w-3" />;
        label = `${v} days without a red flag · milestone 90+`;
    } else if (v >= 30) {
        color = "#2e804f";
        icon = <ShieldCheck className="h-3 w-3" />;
        label = `${v} days without a red flag · milestone 30+`;
    }
    return (
        <div
            className="text-xs flex items-center gap-1.5"
            style={{ color }}
            data-testid="incidents-kpi-safety-streak"
        >
            {icon}
            <span>{label}</span>
        </div>
    );
}

export default function IncidentsKpiTile({ moduleCode, style, className = "" }) {
    const navigate = useNavigate();
    const [kpi, setKpi] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const params = moduleCode ? { module_code: moduleCode } : {};
        api.get("/incidents/kpi", { params })
            .then((r) => { if (!cancelled) setKpi(r.data); })
            .catch((e) => {
                // 403 = user has no incidents access; tile renders empty.
                if (e?.response?.status === 403) {
                    if (!cancelled) setKpi(null);
                    return;
                }
                toast.error(formatApiError(e));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [moduleCode]);

    // Silent skip for users with no incidents permission (403).
    if (!loading && !kpi) return null;

    const totalOpen = kpi?.total_open ?? 0;
    const redFlagsOpen = kpi?.red_flags_open ?? 0;
    const redThisWeek = kpi?.red_flags_this_week ?? 0;
    const awaiting = kpi?.awaiting_investigation ?? 0;
    const daysWithout = kpi?.days_since_last_red_flag;
    const byModule = kpi?.by_module || [];
    const allClear = totalOpen === 0;

    const goToDirectory = () => {
        if (moduleCode) navigate(`/m/${moduleCode.toLowerCase()}/incidents?status=open`);
        else navigate("/incidents?status=open");
    };

    return (
        <div
            className={`asl-bento-tile ${className}`}
            style={{ cursor: "pointer", ...style }}
            onClick={goToDirectory}
            data-testid="incidents-kpi-tile"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="asl-section-title flex items-center gap-2">
                        <AlertTriangle
                            className="h-4 w-4"
                            style={{ color: redFlagsOpen > 0 ? "var(--asl-crimson)" : "var(--asl-text-3)" }}
                        />
                        Incidents & Emergencies
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--asl-text-3)" }}>
                        {moduleCode ? `${moduleCode.toUpperCase()} module scope` : "All modules · HR + Admin view"}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goToDirectory(); }}
                    className="text-xs asl-navy-text hover:underline whitespace-nowrap"
                    data-testid="incidents-kpi-open-directory"
                >
                    Open directory →
                </button>
            </div>

            {loading ? (
                <div className="mt-4">
                    <div className="asl-shimmer w-40 h-10 rounded" />
                    <div className="asl-shimmer w-24 h-4 rounded mt-3" />
                </div>
            ) : (
                <>
                    <div className="mt-3 flex items-baseline gap-8 flex-wrap">
                        <div>
                            <div
                                className="mono font-bold"
                                style={{ fontSize: 40, lineHeight: 1, color: allClear ? "#2e804f" : "var(--asl-navy)" }}
                                data-testid="incidents-kpi-total-open"
                            >
                                {totalOpen}
                            </div>
                            <div className="text-xs asl-text-2 mt-0.5">
                                open incident{totalOpen === 1 ? "" : "s"}
                            </div>
                        </div>
                        <div>
                            <div
                                className="mono font-bold"
                                style={{ fontSize: 28, lineHeight: 1, color: redFlagsOpen > 0 ? "var(--asl-crimson)" : "var(--asl-text-3)" }}
                                data-testid="incidents-kpi-red-flags-open"
                            >
                                {redFlagsOpen}
                            </div>
                            <div className="text-xs asl-text-2 mt-0.5">red flag{redFlagsOpen === 1 ? "" : "s"} open</div>
                        </div>
                        <div>
                            <div
                                className="mono font-bold"
                                style={{ fontSize: 28, lineHeight: 1, color: redThisWeek > 0 ? "var(--asl-crimson)" : "var(--asl-text-3)" }}
                                data-testid="incidents-kpi-red-flags-this-week"
                            >
                                {redThisWeek}
                            </div>
                            <div className="text-xs asl-text-2 mt-0.5">red flag{redThisWeek === 1 ? "" : "s"} this week</div>
                        </div>
                        {!moduleCode && (
                            <div>
                                <div
                                    className="mono font-bold"
                                    style={{ fontSize: 28, lineHeight: 1, color: "var(--asl-navy)" }}
                                    data-testid="incidents-kpi-awaiting-investigation"
                                >
                                    {awaiting}
                                </div>
                                <div className="text-xs asl-text-2 mt-0.5">awaiting investigation</div>
                            </div>
                        )}
                    </div>

                    {allClear && (
                        <div
                            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold"
                            style={{ color: "#2e804f" }}
                            data-testid="incidents-kpi-all-clear"
                        >
                            <ShieldCheck className="h-4 w-4" />
                            All clear ✓
                        </div>
                    )}

                    {!moduleCode && byModule.length > 0 && (
                        <div
                            className="mt-4 pt-3 flex flex-wrap gap-3"
                            style={{ borderTop: "1px solid var(--asl-divider)" }}
                            data-testid="incidents-kpi-by-module"
                        >
                            {byModule.map((m) => (
                                <button
                                    key={m.module_id}
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/m/${m.module_code}/incidents?status=open`);
                                    }}
                                    className="asl-bento-minikpi text-left"
                                    style={{ padding: "6px 10px" }}
                                    data-testid={`incidents-kpi-module-${m.module_code}`}
                                >
                                    <div className="text-[10px] uppercase tracking-wider asl-text-3 font-semibold">
                                        {m.module_code}
                                    </div>
                                    <div className="mono text-sm mt-0.5">
                                        <span style={{ color: "var(--asl-navy)" }}>{m.total_open}</span>
                                        <span className="asl-text-3"> · </span>
                                        <span style={{ color: m.red_flags > 0 ? "var(--asl-crimson)" : "var(--asl-text-3)" }}>
                                            {m.red_flags} red
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="mt-3">
                        <DaysWithoutFooter days={daysWithout} />
                    </div>

                    {!moduleCode && kpi?.email_notifications_configured === false && (
                        <div
                            className="mt-2 text-[11px]"
                            style={{ color: "var(--asl-text-3)" }}
                            data-testid="incidents-kpi-email-unconfigured"
                        >
                            Red-flag email notifications require <code>RESEND_API_KEY</code> + <code>INCIDENT_HR_MAILBOX</code>.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
